/**
 * Shared dimension parser for FloorPlanSketch wall lengths, element
 * width/depth, ceiling height, etc.
 *
 *  parseDimension(input) -> { ok: true, feet: number, label: "6'6\"" }
 *                         | { ok: false, error: string }
 *
 *  Accepts:
 *    6        -> 6'0"
 *    6.5      -> 6'6"
 *    6'6      -> 6'6"
 *    6'6"     -> 6'6"
 *    6ft6in   -> 6'6"
 *    78       -> when <120 treat as inches => 6'6"  (else feet)
 *    2.5m     -> 8'2"  (metres → ft/in)
 */
export function parseDimension(raw) {
  if (raw == null) return { ok: false, error: "empty" };
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return { ok: false, error: "empty" };

  // Metric (metres)
  const m = s.match(/^(\d+(?:\.\d+)?)m$/);
  if (m) {
    const ft = parseFloat(m[1]) * 3.28084;
    return { ok: true, feet: ft, label: formatFeetInches(ft) };
  }

  // "6ft6in" or "6ft"
  const ftin = s.match(/^(\d+(?:\.\d+)?)ft(?:(\d+(?:\.\d+)?)in)?$/);
  if (ftin) {
    const f = parseFloat(ftin[1]);
    const i = ftin[2] ? parseFloat(ftin[2]) : 0;
    const feet = f + i / 12;
    return { ok: true, feet, label: formatFeetInches(feet) };
  }

  // "6'6"" or "6'6" or "6'"
  const tick = s.match(/^(\d+(?:\.\d+)?)'(\d+(?:\.\d+)?)?"?$/);
  if (tick) {
    const f = parseFloat(tick[1]);
    const i = tick[2] ? parseFloat(tick[2]) : 0;
    const feet = f + i / 12;
    return { ok: true, feet, label: formatFeetInches(feet) };
  }

  // Bare number
  const n = parseFloat(s);
  if (!isNaN(n)) {
    // Heuristic: integers ≥ 13 and ≤ 240 are interpreted as inches
    if (Number.isInteger(n) && n >= 13 && n <= 240) {
      const feet = n / 12;
      return { ok: true, feet, label: formatFeetInches(feet) };
    }
    return { ok: true, feet: n, label: formatFeetInches(n) };
  }

  return { ok: false, error: "Unrecognised format" };
}

export function formatFeetInches(feet) {
  if (feet == null || isNaN(feet) || feet < 0) return "0'0\"";
  const f = Math.floor(feet);
  let i = Math.round((feet - f) * 12);
  let outF = f, outI = i;
  if (i === 12) { outF = f + 1; outI = 0; }
  return `${outF}'${outI}"`;
}
